import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/Table/Table';
import { type XeroSyncFinding } from '@/features/reports/xero/api';
import { XERO_SYNC_COPY as COPY } from '@/features/reports/xero/copy';
import {
    adminLink,
    dayText,
    findingName,
    sideText,
    whenText,
} from '@/features/reports/xero/labels';

const C = COPY.columns;

/**
 * The differences (ALS-388), one row each: what differs, what it is about, what each side says, and
 * when it was first found, with Open in Xero and Dismiss or Restore. On a phone each is a card: what
 * differs leads with the document and why beneath it, when it was found trails that line, each side
 * is a labelled line, and the actions take a line of their own at the foot so none is squeezed.
 */
export function FindingList({
    findings,
    mayAct,
    busy,
    onDismiss,
    onRestore,
}: {
    findings: readonly XeroSyncFinding[];
    mayAct: boolean;
    /** The finding an action is under way on, whose buttons wait. */
    busy: number | null;
    /** With the button pressed, which the dialog hands the keyboard back to when it closes. */
    onDismiss: (finding: XeroSyncFinding, opener: HTMLElement) => void;
    onRestore: (finding: XeroSyncFinding) => void;
}) {
    return (
        <Table stack region={COPY.card.title} className="table-stack--wide-labels">
            <TableHeader>
                <TableRow>
                    <TableHead>{C.type}</TableHead>
                    <TableHead>{C.found}</TableHead>
                    <TableHead>{C.about}</TableHead>
                    <TableHead>{C.admin}</TableHead>
                    <TableHead>{C.xero}</TableHead>
                    <TableHead>
                        <span className="sr-only">{C.actions}</span>
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {findings.map((finding) => (
                    <FindingRow
                        key={finding.id}
                        finding={finding}
                        mayAct={mayAct}
                        busy={busy === finding.id}
                        onDismiss={onDismiss}
                        onRestore={onRestore}
                    />
                ))}
            </TableBody>
        </Table>
    );
}

function FindingRow({
    finding,
    mayAct,
    busy,
    onDismiss,
    onRestore,
}: {
    finding: XeroSyncFinding;
    mayAct: boolean;
    busy: boolean;
    /** With the button pressed, which the dialog hands the keyboard back to when it closes. */
    onDismiss: (finding: XeroSyncFinding, opener: HTMLElement) => void;
    onRestore: (finding: XeroSyncFinding) => void;
}) {
    const name = findingName(finding);
    const link = adminLink(finding);
    return (
        <TableRow data-finding={finding.kind}>
            <TableCell data-stack="title" className="align-top">
                <div className="flex min-w-0 flex-col gap-1 py-1 md:max-w-[320px]">
                    <span className="font-medium">{COPY.kinds[finding.kind]}</span>
                    {finding.document ? (
                        <span className="text-mono-sm [overflow-wrap:anywhere]">
                            {finding.document}
                        </span>
                    ) : null}
                    {finding.detail ? (
                        <span className="text-muted-foreground text-xs leading-[17px] [overflow-wrap:anywhere]">
                            {finding.detail}
                        </span>
                    ) : null}
                    {finding.dismissal ? (
                        <span className="text-muted-foreground text-xs leading-[17px] [overflow-wrap:anywhere]">
                            {COPY.dismissedBy(
                                finding.dismissal.by,
                                whenText(finding.dismissal.at),
                                finding.dismissal.reason,
                            )}
                        </span>
                    ) : null}
                </div>
            </TableCell>
            <TableCell
                data-stack="status"
                className="text-muted-foreground align-top text-xs whitespace-nowrap md:text-sm"
            >
                <time dateTime={finding.firstSeenAt}>{dayText(finding.firstSeenAt)}</time>
            </TableCell>
            <TableCell data-stack="meta" data-label={C.about} className="align-top">
                <span className="flex min-w-0 flex-col gap-0.5">
                    {finding.order && link ? (
                        <Link
                            {...link}
                            className="text-mono-sm whitespace-nowrap hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                        >
                            {finding.order.name}
                        </Link>
                    ) : null}
                    {finding.client ? (
                        finding.order || !link ? (
                            <span className="[overflow-wrap:anywhere]">{finding.client.name}</span>
                        ) : (
                            <Link
                                {...link}
                                className="[overflow-wrap:anywhere] hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                            >
                                {finding.client.name}
                            </Link>
                        )
                    ) : null}
                </span>
            </TableCell>
            <TableCell
                data-stack="meta"
                data-label={C.admin}
                className="align-top [overflow-wrap:anywhere]"
            >
                {sideText(finding.admin, finding.currency)}
            </TableCell>
            <TableCell
                data-stack="meta"
                data-label={C.xero}
                className="align-top [overflow-wrap:anywhere]"
            >
                {sideText(finding.xero, finding.currency)}
            </TableCell>
            <TableCell data-stack="action" className="align-top">
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 md:justify-end">
                    {finding.xeroUrl ? (
                        <a
                            href={finding.xeroUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={COPY.openInXeroLabel(name)}
                            className="text-link text-label-sm inline-flex items-center gap-1 whitespace-nowrap hover:underline pointer-coarse:min-h-11 [&_svg]:size-3.5"
                        >
                            {COPY.openInXero}
                            <ExternalLink aria-hidden />
                        </a>
                    ) : null}
                    {mayAct ? (
                        <Button
                            variant="link"
                            size="sm"
                            className="h-auto pointer-coarse:h-11"
                            disabled={busy}
                            // What the panel hands the keyboard to when the row before it leaves the list.
                            data-finding-action={finding.id}
                            aria-label={
                                finding.dismissal
                                    ? COPY.restoreLabel(COPY.kinds[finding.kind], name)
                                    : COPY.dismissLabel(COPY.kinds[finding.kind], name)
                            }
                            onClick={(event) =>
                                finding.dismissal
                                    ? onRestore(finding)
                                    : onDismiss(finding, event.currentTarget)
                            }
                        >
                            {finding.dismissal ? COPY.restore : COPY.dismiss}
                        </Button>
                    ) : null}
                </span>
            </TableCell>
        </TableRow>
    );
}
